import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import { Plus, TrashSimple } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWorkoutAssignment, IWorkout } from '../../../models/programming';
import { ASSIGNMENT_TYPE, ASSIGNMENT_ORIGIN } from '../../../enums/programming';
import AssignWorkoutModal from '../AssignWorkoutModal/AssignWorkoutModal';
import { CoachCompileModal } from '../../DetailTeamPage/ProgrammingTab/CoachCompileModal';
import styles from './TeamProgrammingTab.module.scss';

interface TeamProgrammingTabProps {
  teamId: string;
  athletes?: { _id: string; firstName: string; lastName: string }[];
}

interface CompileModalState {
  open: boolean;
  workout: IWorkout | null;
  assignment: IWorkoutAssignment | null;
  athleteId: string;
  athleteName: string;
}

const TeamProgrammingTab = ({ teamId, athletes = [] }: TeamProgrammingTabProps) => {
  const queryClient = useQueryClient();
  const { getProgrammingAssignments, deleteProgrammingAssignment } = useEndpoints();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [compileModal, setCompileModal] = useState<CompileModalState>({
    open: false,
    workout: null,
    assignment: null,
    athleteId: '',
    athleteName: '',
  });
  // athleteSelector: when a card's Compila is clicked and there are multiple assignees
  const [athleteSelector, setAthleteSelector] = useState<{
    assignment: IWorkoutAssignment;
    workout: IWorkout;
  } | null>(null);

  const today = new Date();
  const [fromDate, setFromDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(
    new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
  );

  const assignmentsQuery = useQuery({
    queryKey: ['programming-assignments', teamId, fromDate, toDate],
    queryFn: async () => {
      const { data } = await getProgrammingAssignments({
        teamId,
        from: fromDate,
        to: toDate,
        limit: 50,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgrammingAssignment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programming-assignments'] }),
  });

  const assignments = useMemo(() => assignmentsQuery.data?.docs ?? [], [assignmentsQuery.data]);

  const getWorkoutTitle = (a: IWorkoutAssignment) => {
    if (typeof a.workoutId === 'object' && a.workoutId !== null) {
      return (a.workoutId as IWorkout).title;
    }
    return 'Scheda';
  };

  const getAthleteName = (id: string) => {
    const found = athletes.find((a) => a._id === id);
    return found ? `${found.firstName} ${found.lastName}` : `...${id.slice(-6)}`;
  };

  const handleCompileClick = (e: React.MouseEvent, assignment: IWorkoutAssignment) => {
    e.stopPropagation();
    const workout =
      typeof assignment.workoutId === 'object' && assignment.workoutId !== null
        ? (assignment.workoutId as IWorkout)
        : null;
    if (!workout) return;

    const athleteAssignees = assignment.assignees.filter((a) => a.type === 'athlete');
    if (athleteAssignees.length === 0) return;

    if (athleteAssignees.length === 1) {
      const athleteId = athleteAssignees[0].id;
      setCompileModal({
        open: true,
        workout,
        assignment,
        athleteId,
        athleteName: getAthleteName(athleteId),
      });
    } else {
      // Multiple athletes — show selector
      setAthleteSelector({ assignment, workout });
    }
  };

  const handleAthleteSelected = (athleteId: string) => {
    if (!athleteSelector) return;
    setCompileModal({
      open: true,
      workout: athleteSelector.workout,
      assignment: athleteSelector.assignment,
      athleteId,
      athleteName: getAthleteName(athleteId),
    });
    setAthleteSelector(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.filtersRow}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className={styles.dateInput}
          />
          <span style={{ color: '#808080', fontSize: 13 }}>—</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className={styles.dateInput}
          />
        </div>
        <button className={styles.assignBtn} onClick={() => setAssignModalOpen(true)}>
          <Plus size={16} weight="bold" />
          Assegna scheda
        </button>
      </div>

      {assignmentsQuery.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : assignments.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Nessuna scheda assegnata in questo periodo</p>
        </div>
      ) : (
        <div className={styles.assignmentsList}>
          {assignments.map((a) => {
            const workoutObj =
              typeof a.workoutId === 'object' && a.workoutId !== null
                ? (a.workoutId as IWorkout)
                : null;
            const canCompile = workoutObj !== null;

            return (
              <div key={a._id} className={styles.assignmentCard}>
                <div className={styles.assignmentLeft}>
                  <p className={styles.assignmentTitle}>{getWorkoutTitle(a)}</p>
                  <div className={styles.assignmentMeta}>
                    <span
                      className={`${styles.badge} ${
                        a.assignmentType === ASSIGNMENT_TYPE.SCHEDULED
                          ? styles.badgeScheduled
                          : styles.badgePlaylist
                      }`}
                    >
                      {a.assignmentType === ASSIGNMENT_TYPE.SCHEDULED ? 'Schedulata' : 'Playlist'}
                    </span>
                    {a.createdFrom === ASSIGNMENT_ORIGIN.FIELD_BLOCK && (
                      <span
                        className={`${styles.badge} ${styles.badgeField}`}
                        title="Scheda creata in un blocco atletico di una sessione Campo"
                      >
                        Campo
                      </span>
                    )}
                    <span>{a.assignees.length} assegnatar{a.assignees.length === 1 ? 'io' : 'i'}</span>
                    {a.scheduledDates && a.scheduledDates.length > 0 && (
                      <span>
                        {new Date(a.scheduledDates[0]).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                        {a.scheduledDates.length > 1 && ` (+${a.scheduledDates.length - 1})`}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {canCompile && (
                    <button
                      className={styles.compileBtn}
                      onClick={(e) => handleCompileClick(e, a)}
                      title="Compila per un atleta"
                    >
                      Compila
                    </button>
                  )}
                  <button
                    className={styles.deleteBtn}
                    onClick={() => {
                      if (window.confirm('Eliminare questa assegnazione?')) {
                        deleteMutation.mutate(a._id);
                      }
                    }}
                    title="Elimina assegnazione"
                  >
                    <TrashSimple size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Athlete selector dropdown when multiple assignees */}
      {athleteSelector && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setAthleteSelector(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--c-card-bg)',
              borderRadius: 12,
              padding: 24,
              minWidth: 280,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: '#1A202C' }}>
              Seleziona atleta
            </div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 16 }}>
              {athleteSelector.workout.title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {athleteSelector.assignment.assignees
                .filter((a) => a.type === 'athlete')
                .map((assignee) => (
                  <button
                    key={assignee.id}
                    onClick={() => handleAthleteSelected(assignee.id)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#F7FAFC',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#2D3748',
                    }}
                  >
                    {getAthleteName(assignee.id)}
                  </button>
                ))}
            </div>
            <button
              onClick={() => setAthleteSelector(null)}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '8px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                backgroundColor: 'var(--c-card-bg)',
                cursor: 'pointer',
                fontSize: 13,
                color: '#718096',
              }}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      <AssignWorkoutModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        workout={null}
      />

      {/* CoachCompileModal */}
      {compileModal.open && compileModal.workout && compileModal.assignment && (
        <CoachCompileModal
          open={compileModal.open}
          workout={compileModal.workout}
          assignment={compileModal.assignment}
          athleteId={compileModal.athleteId}
          athleteName={compileModal.athleteName}
          onClose={() => setCompileModal((prev) => ({ ...prev, open: false }))}
        />
      )}
    </div>
  );
};

export default TeamProgrammingTab;
